'use client';

import clsx from 'clsx';
import { useState } from 'react';

import { useUpdateTaskMutation } from '@/features/tasks/api/tasksApi';
import { SmartInput } from '@web/ui';
import { getErrorMessage, notify } from '@web/utils';

import styles from './EditableTaskTitle.module.scss';

type EditableTaskTitleProps = {
  taskId: string;
  value: string | null;
  canEdit: boolean;
  className?: string;
  onUpdated?: () => void;
};

function getDisplayTitle(value: string | null) {
  return value?.trim() || 'Untitled task';
}

export function EditableTaskTitle({
  taskId,
  value,
  canEdit,
  className,
  onUpdated,
}: EditableTaskTitleProps) {
  const [updateTask, { isLoading }] = useUpdateTaskMutation();
  const [isEditing, setIsEditing] = useState(false);
  const [draftTitle, setDraftTitle] = useState(value ?? '');
  const [error, setError] = useState('');
  const displayTitle = getDisplayTitle(value);

  const cancelEdit = () => {
    setDraftTitle(value ?? '');
    setError('');
    setIsEditing(false);
  };

  const saveTitle = async () => {
    const nextTitle = draftTitle.trim();

    if (!nextTitle) {
      setError('Title is required.');
      return;
    }

    if (nextTitle === (value ?? '').trim()) {
      cancelEdit();
      return;
    }

    try {
      await updateTask({
        taskId,
        payload: {
          title: nextTitle,
        },
      }).unwrap();
      setError('');
      setIsEditing(false);
      onUpdated?.();
    } catch (saveError) {
      notify({ type: 'error', message: getErrorMessage(saveError) });
    }
  };

  if (!canEdit) {
    return (
      <span
        className={clsx(
          styles.editableTaskTitle,
          styles.editableTaskTitle__readonly,
          className,
        )}
      >
        {displayTitle}
      </span>
    );
  }

  if (isEditing) {
    return (
      <span
        className={clsx(styles.editableTaskTitle, className)}
        onClick={(event) => event.stopPropagation()}
      >
        <span className={styles.editableTaskTitle__input}>
          <SmartInput
            type="text"
            name={`task-title-${taskId}`}
            label="Task name"
            value={draftTitle}
            error={error}
            readOnly={isLoading}
            onChange={(nextValue) => {
              setDraftTitle(nextValue);
              setError('');
            }}
            onBlur={() => {
              void saveTitle();
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                void saveTitle();
              }

              if (event.key === 'Escape') {
                event.preventDefault();
                cancelEdit();
              }
            }}
          />
        </span>
      </span>
    );
  }

  return (
    <span className={clsx(styles.editableTaskTitle, className)}>
      <button
        type="button"
        className={styles.editableTaskTitle__button}
        aria-label={`Edit task name ${displayTitle}`}
        onClick={(event) => {
          event.stopPropagation();
          setDraftTitle(value ?? '');
          setError('');
          setIsEditing(true);
        }}
      >
        {displayTitle}
      </button>
    </span>
  );
}
